(() => {
  'use strict';
  if (window.HorizonWishlist) return;

  const STORAGE_KEY = 'horizon-wishlist-v1';
  const root = window.Shopify?.routes?.root || '/';

  function read() {
    try {
      const value = JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]');
      return Array.isArray(value) ? value.filter(item => item && typeof item.handle === 'string') : [];
    } catch (_) { return []; }
  }
  function write(items) {
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(items)); }
    catch (_) { announce('Your browser could not save the wishlist. Check storage settings.'); }
    syncButtons();
    renderPage();
    document.dispatchEvent(new CustomEvent('horizon:wishlist-updated', { detail: { items } }));
  }
  function announce(message) {
    let node = document.querySelector('[data-wishlist-live]');
    if (!node) {
      node = document.createElement('div');
      node.dataset.wishlistLive = '';
      node.className = 'visually-hidden';
      node.setAttribute('role', 'status');
      node.setAttribute('aria-live', 'polite');
      document.body.appendChild(node);
    }
    node.textContent = message;
  }
  function has(handle) { return read().some(item => item.handle === handle); }
  function toggle(button) {
    const handle = button.dataset.productHandle;
    if (!handle) return;
    const items = read();
    const index = items.findIndex(item => item.handle === handle);
    if (index >= 0) {
      items.splice(index, 1);
      write(items);
      announce('Removed from wishlist');
    } else {
      items.push({ handle, url: button.dataset.productUrl || `${root}products/${handle}` });
      write(items);
      announce('Added to wishlist');
    }
  }
  function syncButtons() {
    const items = read();
    document.querySelectorAll('[data-wishlist-button]').forEach(button => {
      const active = items.some(item => item.handle === button.dataset.productHandle);
      button.classList.toggle('is-active', active);
      button.setAttribute('aria-pressed', String(active));
      button.setAttribute('aria-label', `${active ? 'Remove' : 'Add'} ${button.dataset.productTitle || 'product'} ${active ? 'from' : 'to'} wishlist`);
      button.title = active ? 'Remove from wishlist' : 'Add to wishlist';
    });
    document.querySelectorAll('[data-wishlist-count]').forEach(node => {
        node.textContent = items.length > 99 ? '99+' : String(items.length);
        node.hidden = items.length === 0;
    });
  }
  function money(cents, currency) {
    try { return new Intl.NumberFormat(document.documentElement.lang || undefined, { style: 'currency', currency }).format(cents / 100); }
    catch (_) { return (cents / 100).toFixed(2); }
  }
  function element(tag, className, text) {
    const node = document.createElement(tag);
    if (className) node.className = className;
    if (text !== undefined) node.textContent = text;
    return node;
  }
  async function renderPage() {
    const container = document.querySelector('[data-wishlist-page]');
    if (!container) return;
    const grid = container.querySelector('[data-wishlist-grid]');
    const empty = container.querySelector('[data-wishlist-empty]');
    const count = container.querySelector('[data-wishlist-page-count]');
    if (!grid) return;
    const items = read();
    if (count) count.textContent = String(items.length);
    grid.replaceChildren();
    if (!items.length) {
      if (empty) empty.hidden = false;
      return;
    }
    if (empty) empty.hidden = true;
    const currency = container.dataset.currency || 'USD';
    const results = await Promise.all(items.map(async item => {
      try {
        const response = await fetch(`${root}products/${encodeURIComponent(item.handle)}.js`);
        if (!response.ok) return null;
        return await response.json();
      } catch (_) { return null; }
    }));
    const current = read();
    results.forEach((product, index) => {
      const item = items[index];
      if (!product || !current.some(saved => saved.handle === item.handle)) return;
      const card = element('article', 'horizon-wishlist-card');
      const link = element('a', 'horizon-wishlist-card__image-link');
      link.href = product.url || item.url;
      link.setAttribute('aria-label', product.title);
      if (product.featured_image) {
        const img = element('img', 'horizon-wishlist-card__image');
        img.src = product.featured_image;
        img.alt = product.title || '';
        img.loading = 'lazy';
        link.appendChild(img);
      } else {
        link.appendChild(element('div', 'horizon-wishlist-card__image-placeholder', 'Image unavailable'));
      }
      const details = element('div', 'horizon-wishlist-card__details');
      const title = element('a', 'horizon-wishlist-card__title', product.title);
      title.href = product.url || item.url;
      details.appendChild(title);
      details.appendChild(element('p', 'horizon-wishlist-card__price', money(product.price, currency)));
      const actions = element('div', 'horizon-wishlist-card__actions');
      const variant = (product.variants || []).find(v => v.available) || (product.variants || [])[0];
      const add = element('button', 'horizon-wishlist-card__add', variant && variant.available ? 'Add to cart' : 'Sold out');
      add.type = 'button';
      add.disabled = !variant || !variant.available;
      add.addEventListener('click', async () => {
        if (!variant) return;
        add.disabled = true;
        add.textContent = 'Adding…';
        try {
          const response = await fetch(`${root}cart/add.js`, {
            method: 'POST', headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
            body: JSON.stringify({ items: [{ id: variant.id, quantity: 1 }] })
          });
          if (!response.ok) {
            const error = await response.json().catch(() => ({}));
            throw new Error(error.description || error.message || 'Could not add product to cart');
          }
          add.textContent = 'Added';
          announce(`${product.title} added to cart`);
          document.dispatchEvent(new CustomEvent('cart:refresh'));
          setTimeout(() => { if (add.isConnected) { add.textContent = 'Add to cart'; add.disabled = false; } }, 1400);
        } catch (error) {
          add.textContent = 'Try again';
          announce(error.message || 'Could not add product to cart');
          setTimeout(() => { if (add.isConnected) { add.textContent = 'Add to cart'; add.disabled = !variant.available; } }, 1800);
        }
      });
      const remove = element('button', 'horizon-wishlist-card__remove', 'Remove');
      remove.type = 'button';
      remove.addEventListener('click', () => {
        write(read().filter(saved => saved.handle !== item.handle));
        announce(`${product.title} removed from wishlist`);
      });
      actions.append(add, remove);
      details.appendChild(actions);
      card.append(link, details);
      grid.appendChild(card);
    });
    if (!grid.children.length && empty) empty.hidden = false;
  }
  function init() {
    document.addEventListener('click', event => {
      const button = event.target.closest('[data-wishlist-button]');
      if (button) { event.preventDefault(); event.stopPropagation(); toggle(button); }
    });
    syncButtons();
    renderPage();
  }
  window.HorizonWishlist = { getItems: read, add(handle, url) { const items = read(); if (!items.some(item => item.handle === handle)) { items.push({ handle, url }); write(items); } }, remove(handle) { write(read().filter(item => item.handle !== handle)); }, sync: syncButtons };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init, { once: true }); else init();
  document.addEventListener('shopify:section:load', () => { syncButtons(); renderPage(); });
})();
